import React, { Fragment } from "react";
import { Transition, Dialog } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";

export interface FlyoutControlProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export interface FlyoutProps extends FlyoutControlProps {
  title: string;
  children: React.ReactNode;
  disableClickAway?: boolean;
}

/**
 * Wrapper for creating flyouts
 * @param {boolean} open flyout open or closed (shown or hidden)
 * @param {Function} setOpen set the visability of the flyout 
 * @param {string} title title to place at the top of the flyout
 * @param {JSX.Element[]} children content to render in the flyout
 * @param {boolean} [disableClickAway=false] if true, clicking outside the flyout will not close it
 */
export default function Flyout({
  open,
  setOpen,
  title,
  children,
  disableClickAway = false,
}: FlyoutProps) {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 overflow-hidden z-20"
        onClose={!disableClickAway ? setOpen : () => { }}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0" />
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="pointer-events-auto w-screen max-w-3xl">
                <div className="flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                  <div className="px-4 sm:px-8">
                    <div className="flex items-start justify-between">
                      <Dialog.Title className="text-xl font-medium text-gray-900">
                        {title}
                      </Dialog.Title>
                      <div className="flex h-7 items-center">
                        <button
                          type="button"
                          className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                          onClick={() => setOpen(false)}
                        >
                          <span className="sr-only">Close</span>
                          <XIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-6">{children}</div>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
